// src/lib/intake/intakeApi.js
//
// Ticket 061 (Advisor Intake I4), widened by ticket 069 (I7). The ONLY place the
// browser calls the intake functions: intake-start, intake-load, intake-save
// (ticket 059), intake-calculate (live preview, reads saved facts, writes nothing)
// and intake-submit (the explicit "Save my results" freeze), and intake-share
// (ticket 070: status, share with the linked advisor, stop sharing), and
// intake-clients (ticket 073: an advisor's own clients).
//
// Ticket 073: in ADVISOR mode the five intake calls also carry `subjectId` (the
// client the advisor owns; the server checks ownership on every call and the
// account type decides the mode, never the body). A client-mode call never sends
// one. The id goes in the request body only, never in a URL.
//
// Every call is a user-authenticated fetch (Authorization: Bearer <access token>),
// the same pattern as src/lib/claimSession.js. The functions are deployed WITHOUT
// --no-verify-jwt. The body never carries an owner, a source or
// results. Client mode: the server finds the caller's own subject from the
// verified token. Advisor mode: the body names the advisor's client (subject_id).
//
// Returns { ok, status, body, kind } and never throws. kind is one of:
//   'OK', 'REVISION_CONFLICT', 'PARTNER_ACCOUNT', 'INVALID_TAX_YEAR',
//   'NO_INTAKE' (404 with that code: nothing saved for the year),
//   'RESULTS_UNAVAILABLE' (422: the saved facts could not be calculated),
//   'NO_CLIENT' (advisor mode: not found or not yours), 'CLIENT_LIMIT', 'CLIENT_ID_TAKEN',
//   'SUBJECT_REQUIRED' / 'SUBJECT_NOT_ALLOWED' / 'NOT_ADVISOR' / 'HAS_CONSENT_HISTORY' (ticket 073),
//   'NO_ADVISOR' / 'NOT_SAVED' / 'INVALID_NAME' / 'INVALID_PHONE' (intake-share, ticket 070),
//   'NOT_AVAILABLE' (404: server switch off), 'UNAUTHORIZED' (401),
//   'BAD_REQUEST' (other 4xx), 'SERVER' (5xx), 'NETWORK' (fetch failed).

function classify(status, body) {
  if (status >= 200 && status < 300) return 'OK'
  const code = body && typeof body.error === 'string' ? body.error : ''
  if (code === 'REVISION_CONFLICT') return 'REVISION_CONFLICT'
  if (code === 'PARTNER_ACCOUNT') return 'PARTNER_ACCOUNT'
  if (code === 'INVALID_TAX_YEAR') return 'INVALID_TAX_YEAR'
  // These two must come BEFORE the generic 404, or a missing intake would look
  // like the switch being off.
  if (code === 'NO_INTAKE') return 'NO_INTAKE'
  if (code === 'RESULTS_UNAVAILABLE') return 'RESULTS_UNAVAILABLE'
  // intake-share (ticket 070): named refusals the share screen explains.
  if (code === 'NO_ADVISOR' || code === 'NOT_SAVED' || code === 'INVALID_NAME' || code === 'INVALID_PHONE') return code
  if (code === 'INVALID_CONTACT') return 'INVALID_NAME'
  // Ticket 073. NO_CLIENT is a 404 too, so it must come before the generic 404.
  if (code === 'NO_CLIENT' || code === 'CLIENT_LIMIT' || code === 'CLIENT_ID_TAKEN' || code === 'SUBJECT_REQUIRED'
    || code === 'SUBJECT_NOT_ALLOWED' || code === 'NOT_ADVISOR' || code === 'HAS_CONSENT_HISTORY'
    || code === 'PARTNER_REF_NOT_ALLOWED') return code
  if (status === 404) return 'NOT_AVAILABLE'
  if (status === 401) return 'UNAUTHORIZED'
  if (status >= 500) return 'SERVER'
  return 'BAD_REQUEST'
}

async function call(name, accessToken, payload) {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${name}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      }
    )
    let body = null
    try { body = await res.json() } catch { body = null }
    return { ok: res.ok, status: res.status, body, kind: classify(res.status, body) }
  } catch (err) {
    console.error(`${name} error:`, err)
    return { ok: false, status: 0, body: null, kind: 'NETWORK' }
  }
}

// partnerRef is sent only when non-empty (the server limit is 100 characters).
// subjectId: advisor mode only (a client never sends one).
const withSubject = (payload, subjectId) => (subjectId ? { ...payload, subject_id: subjectId } : payload)

export function startIntake({ accessToken, taxYear, domains, partnerRef, subjectId }) {
  const payload = { tax_year: taxYear, domains }
  if (typeof partnerRef === 'string' && partnerRef.length > 0) payload.partner_ref = partnerRef
  return call('intake-start', accessToken, withSubject(payload, subjectId))
}

export function loadIntake({ accessToken, taxYear, subjectId }) {
  return call('intake-load', accessToken, withSubject({ tax_year: taxYear }, subjectId))
}

export function saveIntake({ accessToken, taxYear, baseRevision, domains, subjectId }) {
  return call('intake-save', accessToken, withSubject({ tax_year: taxYear, base_revision: baseRevision, domains }, subjectId))
}

// Ticket 069. The body carries the year only: the server reads the SAVED facts.
// Response: the masked results, with meta.facts_revision.
export function calculateIntake({ accessToken, taxYear, subjectId }) {
  return call('intake-calculate', accessToken, withSubject({ tax_year: taxYear }, subjectId))
}

// Ticket 069. Freezes the saved facts at baseRevision (the preview's
// meta.facts_revision). Response: { submission_id, already_submitted }.
export function submitIntake({ accessToken, taxYear, baseRevision, subjectId }) {
  return call('intake-submit', accessToken, withSubject({ tax_year: taxYear, base_revision: baseRevision }, subjectId))
}

// Ticket 070. The body names the year and the action only (plus the revision and
// contact for a share): never an advisor, a subject, the consent text or an email.
// The server finds the advisor from the stored record and the email from the token.
export function getShareStatus({ accessToken, taxYear }) {
  return call('intake-share', accessToken, { tax_year: taxYear, action: 'status' })
}

// contact: { name, phone? }. factsRevision: the revision of the saved results.
export function shareIntake({ accessToken, taxYear, factsRevision, contact }) {
  return call('intake-share', accessToken, { tax_year: taxYear, action: 'share', facts_revision: factsRevision, contact })
}

export function stopSharing({ accessToken, taxYear }) {
  return call('intake-share', accessToken, { tax_year: taxYear, action: 'stop' })
}

// ── An advisor's own clients (ticket 073, intake-clients) ───────────────────
// Advisor logins only (a client gets 403 NOT_ADVISOR). The owner is the verified
// token; the body never names one.
export function listClients({ accessToken }) {
  return call('intake-clients', accessToken, { action: 'list' })
}

// clientRequestId: a random uuid made once per "New client" form, so a double click
// or a retry creates one client, not two.
export function createClient({ accessToken, clientRequestId, name }) {
  return call('intake-clients', accessToken, { action: 'create', client_request_id: clientRequestId, name })
}

export function renameClient({ accessToken, subjectId, name }) {
  return call('intake-clients', accessToken, { action: 'rename', subject_id: subjectId, name })
}

export function removeClient({ accessToken, subjectId }) {
  return call('intake-clients', accessToken, { action: 'remove', subject_id: subjectId })
}
